<template>
  <InputSkeleton :name="name"
                 :errors="errors"
                 class="DateTimePickerInput"
                 :class="{
                   [`DateTimePickerInput--${size}`]: !!size,
                   'DateTimePickerInput--disabled': disabled,
                   'DateTimePickerInput--readonly': readonly,
                   'DateTimePickerInput--invalid': invalid || !!errors.length,
                   'DateTimePickerInput--highlight': isHighlighted,
                 }">
    <template v-if="($slots.default || isStingLabel && label)" #label>
      <slot v-if="$slots.default" />
      <template v-else>{{ label }}</template>
    </template>
    <div class="DateTimePickerInput__container">
      <DatePickerInput v-model="date"
                       :size="size"
                       :date-min="dateMin"
                       :date-max="dateMax"
                       :placeholder="datePlaceholder"
                       :autofocus="autofocus"
                       :disabled="disabled"
                       :readonly="readonly"
                       :invalid="invalid || Boolean(errors.length)"
                       :highlight="highlight"
                       :name="`Datetimepicker-${name}`"
                       :float-label="floatLabel"
                       :label="isDateTimeTitles(label) ? label.date : ''"
                       :is-prefix-date="isPrefixDate"
                       @update:model-value="onChangeDate"
      />
      <div v-if="optionalTime && !showTimepicker" class="DateTimePickerInput__time-container">
        <button type="button" class="btn btn--link" @click="onToggleTimepicker">
          {{ optionalTimeText }}
        </button>
      </div>
      <div v-else class="DateTimePickerInput__time-container">
        <TimePickerInput v-model="time"
                         :size="size"
                         :placeholder="timePlaceholder"
                         :disabled="disabled"
                         :readonly="readonly"
                         :invalid="invalid || Boolean(errors.length)"
                         :highlight="highlight"
                         :name="`Timepicker-${name}`"
                         :float-label="floatLabel"
                         :label="isDateTimeTitles(label) ? label.time : ''"
                         @update:model-value="onChangeTime"
                         @blur="onBlurTime"
        />
        <button v-if="!disabled && !readonly"
                type="button"
                class="DateTimePickerInput__remove"
                @click="onClear">
          <Icon name="cross" />
        </button>
      </div>
    </div>
  </InputSkeleton>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import { computed, ref, watch } from 'vue';
  import { UiSize } from '../../../constants';
  import Icon from '../../Icon.vue';
  import InputSkeleton from '../InputSkeleton.vue';
  import DatePickerInput from './DatePickerInput.vue';
  import TimePickerInput from './TimePickerInput.vue';
  import { InputErrorObject, DateTimeTitles, isDateTimeTitles } from './types';

  const props = withDefaults(defineProps<{
    modelValue: string;
    name: string;
    label?: string | DateTimeTitles;
    datePlaceholder?: string;
    timePlaceholder?: string;
    size?: UiSize;
    autofocus?: boolean;
    invalid?: boolean;
    errors?: InputErrorObject[];
    disabled?: boolean;
    readonly?: boolean;
    highlight?: boolean;
    dateMin?: string;
    dateMax?: string;
    optionalTime?: boolean;
    optionalTimeText?: string;
    floatLabel?: boolean;
    isPrefixDate?: boolean;
  }>(), {
    autofocus: false,
    optionalTime: false,
    optionalTimeText: 'указать время',
    size: UiSize.MD,
    datePlaceholder: 'дд.мм.гггг',
    timePlaceholder: 'чч:мм',
    dateWidth: '65%',
    timeWidth: '35%',
    errors: () => [],
    floatLabel: false,
  });

  const emit = defineEmits<{
    (e: 'update:model-value', modelValue: string): void;
    (e: 'update:time-value', time: string): void;
  }>();

  const FORMAT_TIME = 'HH:mm';
  const DEFAULT_TIME = '12:00';

  dayjs.extend(utc);

  const showTimepicker = ref<boolean>(false);
  const date = ref<string>('');
  const time = ref<string>('');

  const isHighlighted = computed<boolean>(() => Boolean(props.highlight && !props.modelValue));
  const isStingLabel = computed(() => typeof props.label === 'string');

  const onToggleTimepicker = () => {
    showTimepicker.value = true;
  };

  const concatDate = (dateVal: string, timeVal: string): string => {
    const [hours, minutes] = (timeVal || DEFAULT_TIME).split(':').map((value) => Number(value));

    return dayjs(dateVal).local().set('hour', hours).set('minute', minutes).toISOString();
  };

  const onClear = () => {
    if (props.optionalTime) {
      onToggleTimepicker();
      time.value = '';
      showTimepicker.value = false;
    } else {
      time.value = '';
      emit('update:model-value', '');
    }

    emit('update:time-value', '');
  };

  const onChangeTime = () => {
    if (time.value && time.value.trim().length === FORMAT_TIME.length && date.value) {
      emit('update:model-value', concatDate(date.value, time.value));
    }

    emit('update:time-value', time.value);
  };

  const onBlurTime = () => {
    time.value = dayjs(props.modelValue).format(FORMAT_TIME);
  };

  const onChangeDate = () => {
    if (date.value) {
      emit('update:model-value', concatDate(date.value, time.value));
    } else {
      emit('update:model-value', '');
    }
  };

  watch(() => props.modelValue, () => {
    if (props.modelValue) {
      date.value = dayjs(props.modelValue).startOf('day').toISOString();

      if (!props.optionalTime) {
        time.value = dayjs(props.modelValue).format(FORMAT_TIME);
      }
    } else {
      date.value = '';
      time.value = '';
    }
  }, {
    immediate: true,
  });
</script>

<style lang="scss" scoped>
  @import '../../../assets/scss/_variables.scss';
  @import '../../../assets/scss/_mixins.scss';

  .DateTimePickerInput {
    display: flex;
    flex-direction: column;
    width: 100%;

    &__container {
      display: grid;
      grid-template-columns: 2fr 1fr;
      column-gap: 10px;
    }

    &__remove {
      background-color: initial;
      border: 0;
      cursor: pointer;

      svg {
        display: block;
        width: 12px;
        height: 12px;
        color: $color-grey-100;
      }
    }

    &__time-container {
      display: grid;
      grid-template-columns: 1fr 22px;
      justify-content: flex-start;
      min-width: 90px;
    }
  }
</style>
